// BlockSettingsPanel — inline accordion for editing a single block's
// `data` JSON + `title` (per `docs/spec/wishlist/DESIGN.md §7.2`).
//
// 6 block types:
//   - progress: mode (percent / amount) + target + currency + label
//   - budget:   amount + currency + label
//   - notes:    body (textarea 4 rows, markdown)
//   - checklist / link / photo: read-only "已加 N 項" + manage button
//     (W34.2.1: button navigates to /wishes/{wid}?block={bid} so the
//     detail page can scroll + highlight that block)
//   - 通用: block title (required when same type has multiple blocks)
//
// Storage pattern (per spec): explicit save button per panel — no
// auto-save. PATCH /api/v1/wishes/{wid}/blocks/{bid} with { data, title }.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/patterns/ConfirmDialog";
import { cn } from "@/lib/utils/cn";
import { useJsonDraft } from "@/lib/hooks/useDraft";
import type { WishBlockOut } from "@/lib/api/wishlist";
import { blockTypeLabel } from "@/lib/wish/blockTypeLabel";

export interface BlockSettingsPanelProps {
  block: WishBlockOut;
  /** Number of other blocks of the same type — drives the title-required rule. */
  sameTypeCount: number;
  /** Whether the title field is required (same type has 2+ blocks, per DATA_MODEL §8.3). */
  titleRequired: boolean;
  /** Save handler — fires only when the user clicks the Save button. */
  onSave: (input: { title: string; data: Record<string, unknown> }) => void;
  /** Delete handler — fires after a confirmation. */
  onDelete: () => void;
  /** Optional indicator for "saving in flight" (disables Save). */
  saving?: boolean;
  className?: string;
}

const PROGRESS_MODE_LABELS: Record<string, string> = {
  percent: "百分",
  amount: "金額",
};

export function BlockSettingsPanel({
  block,
  titleRequired,
  onSave,
  onDelete,
  saving,
  className,
}: BlockSettingsPanelProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // W39.76 — the same class as the other five compose surfaces: a session
  // revoked mid-sentence takes React state with it, and rotate-on-action means
  // that happens on a second phone as a matter of course.
  //
  // **Wired without a measurement, and the one surface in this project whose
  // fix is not backed by a round trip.** Reaching this editor takes two
  // navigation steps (into the layout editor, then expanding the accordion) and
  // `probe-session-expiry.mjs` takes one opener, so measuring it properly means
  // teaching the probe a second step. Every other surface here was measured
  // losing the text before being fixed; this one was not, and saying so is
  // better than implying otherwise.
  const blockDraft = useJsonDraft(`wish-block:${block.id}`, {
    title: block.title,
    data: { ...(block.data ?? {}) } as Record<string, unknown>,
  });
  const [title, setTitle] = useState(blockDraft.value.title);
  const [data, setData] = useState<Record<string, unknown>>(blockDraft.value.data);
  // W49.0 §3. The red border used to be a prop computed by LayoutEditor from
  // `block.title` — the *server's* copy. Clearing the field left `block.title`
  // unchanged until the next save, so the border never lit and the only thing
  // the user got was a save button that silently refused. It has to read the
  // state the user is typing into, which lives here and nowhere else.
  const titleHasError = titleRequired && !title.trim();
  // W34.3.2: replaced `window.confirm(...)` with a controlled
  // ConfirmDialog (auto-inherits BottomSheet on mobile per W34.3.1).
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Reset local form state when the upstream block changes (e.g. server
  // re-fetch after a save) — but a draft outranks the fetched copy, for the
  // same reason everywhere else: a draft only exists because someone was
  // interrupted writing it.
  useEffect(() => {
    setTitle(blockDraft.value.title || block.title);
    const drafted = blockDraft.value.data;
    setData(Object.keys(drafted ?? {}).length ? drafted : { ...(block.data ?? {}) });
    // `blockDraft.value` is read once, in the hook's lazy initialiser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, block.title, block.data]);

  const onChangeData = (key: string, value: unknown) =>
    setData((prev) => {
      const next = { ...prev, [key]: value };
      blockDraft.save({ title, data: next });
      return next;
    });

  const onClickSave = () => {
    // Cleared only after `onSave` resolves; this panel closes on save and
    // clearing on unmount is the thing that would make the draft pointless.
    blockDraft.clear();
    onSave({ title: title.trim(), data });
  };

  return (
    <section
      data-component="BlockSettingsPanel"
      data-block-id={block.id}
      data-block-type={block.block_type}
      data-testid={`block-settings-panel-${block.id}`}
      className={cn(
        "bg-paper-elevated border border-hairline rounded-2xl overflow-hidden",
        className,
      )}
    >
      <header className="flex items-center gap-2 px-s-4 py-s-3">
        <span
          data-testid="block-settings-title-display"
          className="text-body-sm font-semibold text-ink-1 flex-1 truncate"
        >
          {block.title || <span className="text-ink-3 italic">未命名區塊</span>}
        </span>
        <span
          data-testid="block-settings-type-badge"
          className="text-meta text-ink-3"
        >
          {blockTypeLabel(block.block_type)}
        </span>
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          aria-expanded={open}
          aria-controls={`block-settings-body-${block.id}`}
          data-testid={`block-settings-toggle-${block.id}`}
          className="press min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-2 hover:bg-paper-deep"
        >
          {open ? (
            <ChevronUp className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden />
          )}
        </button>
      </header>
      {open ? (
        <div
          id={`block-settings-body-${block.id}`}
          data-component="BlockSettingsPanelBody"
          className="border-t border-hairline px-s-4 py-s-3 flex flex-col gap-s-3"
        >
          <Field
            label="區塊標題"
            required={titleRequired}
            error={titleHasError}
            hint={
              titleRequired
                ? "同類型已有其他區塊，必須填寫有意義的標題"
                : undefined
            }
          >
            <input
              type="text"
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="block-settings-title-input"
              className={cn(
                "w-full h-10 px-3 rounded-md border bg-paper-elevated text-ink-1",
                titleHasError
                  ? "border-ink-1 ring-1 ring-ink-1"
                  : "border-hairline",
              )}
            />
          </Field>

          {block.block_type === "progress" ? (
            <ProgressFields data={data} onChange={onChangeData} />
          ) : null}
          {block.block_type === "budget" ? (
            <BudgetFields data={data} onChange={onChangeData} />
          ) : null}
          {block.block_type === "notes" ? (
            <NotesFields data={data} onChange={onChangeData} />
          ) : null}
          {block.block_type === "checklist" ||
          block.block_type === "link" ||
          block.block_type === "photo" ? (
            <ReadOnlySummary
              block={block}
              onManage={() =>
                navigate(`/wishes/${block.wish_id}?block=${block.id}`)
              }
            />
          ) : null}

          <div className="flex items-center justify-between gap-s-2 pt-s-2 border-t border-hairline">
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              data-testid={`block-settings-delete-${block.id}`}
              className="press inline-flex items-center gap-1.5 min-h-11 px-s-3 rounded-md text-ink-1 hover:bg-paper-deep"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              刪除區塊
            </button>
            <button
              type="button"
              onClick={onClickSave}
              disabled={saving || (titleRequired && !title.trim())}
              data-testid={`block-settings-save-${block.id}`}
              className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full bg-ink-1 text-paper text-body-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="h-4 w-4" aria-hidden />
              儲存
            </button>
          </div>
        </div>
      ) : null}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        destructive
        title="刪除區塊"
        description={`確定刪除「${block.title || block.block_type}」？此操作無法復原。`}
        confirmLabel="刪除區塊"
        onConfirm={() => {
          setDeleteOpen(false);
          onDelete();
        }}
      />
    </section>
  );
}

// --- per-type fields ----------------------------------------------------

function Field({
  label,
  required,
  error,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  error?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label
      data-component="BlockSettingsField"
      className="flex flex-col gap-1"
    >
      <span className="text-meta text-ink-2 font-semibold">
        {label}
        {required ? (
          <span data-testid="field-required" className="text-ink-1 ml-1">
            *
          </span>
        ) : null}
      </span>
      {children}
      {hint ? (
        <span
          data-testid="field-hint"
          className={cn(
            "text-meta",
            error ? "text-ink-1" : "text-ink-3",
          )}
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}

function ProgressFields({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const mode = (data.mode as string) ?? "percent";
  return (
    <>
      <Field label="模式">
        <div role="radiogroup" className="flex gap-2">
          {(["percent", "amount"] as const).map((m) => (
            <label
              key={m}
              className="press flex-1 min-h-11 flex items-center justify-center gap-2 px-3 rounded-md border border-hairline cursor-pointer has-[:checked]:bg-ink-1 has-[:checked]:text-paper"
            >
              <input
                type="radio"
                name="progress-mode"
                value={m}
                checked={mode === m}
                onChange={() => onChange("mode", m)}
                data-testid={`progress-mode-${m}`}
                className="sr-only"
              />
              {PROGRESS_MODE_LABELS[m] ?? m}
            </label>
          ))}
        </div>
      </Field>
      {mode === "amount" ? (
        <>
          <Field label="目標金額">
            <input
              type="number"
              min={0}
              value={(data.target_amount as number) ?? ""}
              onChange={(e) =>
                onChange("target_amount", Number(e.target.value))
              }
              data-testid="progress-target-amount"
              className="w-full h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
            />
          </Field>
          <Field label="貨幣">
            <select
              value={(data.currency as string) ?? "TWD"}
              onChange={(e) => onChange("currency", e.target.value)}
              data-testid="progress-currency"
              className="w-full h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
            >
              {["TWD", "USD", "JPY", "EUR", "GBP", "HKD", "CNY"].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
        </>
      ) : null}
      <Field label="標籤（選填）">
        <input
          type="text"
          value={(data.label as string) ?? ""}
          onChange={(e) => onChange("label", e.target.value)}
          maxLength={50}
          data-testid="progress-label"
          className="w-full h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
        />
      </Field>
    </>
  );
}

function BudgetFields({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <>
      <Field label="預算金額">
        <input
          type="number"
          min={1}
          value={(data.amount as number) ?? ""}
          onChange={(e) => onChange("amount", Number(e.target.value))}
          data-testid="budget-amount"
          className="w-full h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
        />
      </Field>
      <Field label="貨幣">
        <select
          value={(data.currency as string) ?? "TWD"}
          onChange={(e) => onChange("currency", e.target.value)}
          data-testid="budget-currency"
          className="w-full h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
        >
          {["TWD", "USD", "JPY", "EUR", "GBP", "HKD", "CNY"].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="標籤（選填）">
        <input
          type="text"
          value={(data.label as string) ?? ""}
          onChange={(e) => onChange("label", e.target.value)}
          maxLength={50}
          data-testid="budget-label"
          className="w-full h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
        />
      </Field>
    </>
  );
}

function NotesFields({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <Field label="內容（Markdown）">
      <textarea
        rows={4}
        value={(data.body as string) ?? ""}
        onChange={(e) => onChange("body", e.target.value)}
        data-testid="notes-body"
        className="w-full p-3 rounded-md border border-hairline bg-paper-elevated text-ink-1 font-mono text-meta resize-y"
      />
    </Field>
  );
}

function ReadOnlySummary({
  block,
  onManage,
}: {
  block: WishBlockOut;
  onManage: () => void;
}) {
  return (
    <div
      data-testid={`block-settings-readonly-${block.id}`}
      className="text-meta text-ink-2 px-s-3 py-s-2 bg-paper-deep rounded-md flex items-center justify-between gap-s-2"
    >
      <span>
        子表項目在詳情頁管理（{blockTypeLabel(block.block_type)} 區塊）
      </span>
      <button
        type="button"
        onClick={onManage}
        data-testid={`block-settings-manage-${block.id}`}
        className="press text-you-strong underline min-h-11 px-s-3 rounded-full"
      >
        管理
        <ArrowRight size={14} aria-hidden className="ml-1 inline" />
      </button>
    </div>
  );
}
